import { trpc } from '../lib/trpc.js';
import { useAuth } from '../lib/auth.js';

/**
 * The signed-in person's real display name, regardless of which side
 * they're on. Falls back to the email prefix while the real name is
 * loading, so the UI never flashes empty.
 *
 * Agency users come from User.name (via trpc.user.me); contacts come
 * from ClientContact.name (via trpc.clientContact.me). Both queries
 * are React-Query-cached so dropping this hook into multiple
 * components is cheap.
 */
export function useDisplayName(): {
  name: string;
  firstName: string;
  initials: string;
  email: string;
} {
  const { auth } = useAuth();
  const userMeQ = trpc.user.me.useQuery(undefined, {
    enabled: auth.kind === 'agency',
    staleTime: 5 * 60_000,
  });
  const contactMeQ = trpc.clientContact.me.useQuery(undefined, {
    enabled: auth.kind === 'contact',
    staleTime: 5 * 60_000,
  });

  const email = auth.kind === 'agency' || auth.kind === 'contact' ? auth.email : '';
  const fallback = email.split('@')[0] ?? 'there';
  const name =
    auth.kind === 'agency'
      ? (userMeQ.data?.name ?? fallback)
      : auth.kind === 'contact'
        ? (contactMeQ.data?.name ?? fallback)
        : fallback;
  const firstName = name.split(/\s+/)[0] ?? name;
  const initials =
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || email.slice(0, 2).toUpperCase();

  return { name, firstName, initials, email };
}
